import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";

import {
  Container,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import {
  Person as PersonIcon,
  Chat as ChatIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";

import { Message, RootState } from "types";

import { formatDate } from "utils/helperFunctions";
import { useScrollPosition } from "app/Hooks/useScrollPosition";
import {
  getPastMsgs,
  getFutureMsgs,
} from "_DatabaseFunctions/directConnect/messages";
import SendPM from "./sections/SendPM";

// Extended message interface for display purposes
interface DisplayMessage extends Message {
  class?: string;
}

function ConversationLog() {
  const user = useSelector((state: RootState) => state.user.user);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState<{
    currPos: { y: number };
    prevPos: { y: number };
  } | null>(null);
  useScrollPosition(setPosition);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [newMessages, setNewMessages] = useState<DisplayMessage[]>([]);
  const [firstFetch, setFirstFetch] = useState(false);
  const [fetchedAll, setFetchedAll] = useState(false);
  const [fetchTime, setFetchTime] = useState(Date.now());

  const params = useParams<{ userId: string }>();
  const location = useLocation() as { state: { receiverName: string } };

  useEffect(() => {
    fetchMessages();
    awaitFutureMsgs();
  }, [params.userId]);

  useEffect(() => {
    if (!position) return;
    const { currPos, prevPos } = position;
    // fetch older messages when user scrolls to top
    if (!fetchedAll && currPos.y > prevPos.y && currPos.y == 0) {
      (async () => {
        setTimeout(() => {
          fetchMessages();
          window.scrollTo({ top: 600, left: 0, behavior: "smooth" });
        }, 100);
      })();
    }
  }, [position]);

  useEffect(() => {
    if (firstFetch === false) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); // scroll to bottom
      setTimeout(() => {
        // executed after the scroll is completed
        setFirstFetch(true);
      }, 1000);
    }
  }, [messages]);

  const fetchMessages = async () => {
    if (!user || !params.userId) return;
    await setLoading(true);
    await getPastMsgs({
      userId: user.uid,
      receiverId: params.userId,
      endTime: fetchTime,
      messages,
      setMessages,
      setEndTime: setFetchTime,
      setFetchedAll,
    });
    await setLoading(false);
  };

  const awaitFutureMsgs = () => {
    if (!user || !params.userId) return;
    getFutureMsgs({
      userId: user.uid,
      receiverId: params.userId,
      cb: (msgs: DisplayMessage[]) => setNewMessages(msgs),
    });
  };

  const renderMessage = (msg: DisplayMessage, index: number) => {
    const isSelfConversation = params.userId === user?.uid;
    const isCurrentUser = isSelfConversation 
      ? index % 2 === 1 // Show received messages on top (odd indexes as sent)
      : msg.senderId === user?.uid;

    return (
      <Box
        key={`${msg.timeStamp}-${msg.class}-${index}`}
        sx={{
          display: "flex",
          justifyContent: isCurrentUser ? "flex-end" : "flex-start",
          mb: 2,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            maxWidth: "70%",
            padding: 2,
            backgroundColor: isCurrentUser ? "primary.main" : "grey.100",
            color: isCurrentUser ? "primary.contrastText" : "text.primary",
            borderRadius: 2,
            borderBottomRightRadius: isCurrentUser ? 0.5 : 2,
            borderBottomLeftRadius: isCurrentUser ? 2 : 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Avatar sx={{ width: 24, height: 24 }}>
              <PersonIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">
              {msg.senderName}
            </Typography>
          </Box>

          <Box
            sx={{ mb: 1 }}
            dangerouslySetInnerHTML={{ __html: msg.content }}
          />

          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              opacity: 0.7,
              fontSize: "0.75rem",
            }}
          >
            <TimeIcon sx={{ fontSize: 12 }} />
            {formatDate(msg.timeStamp)}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const allMessages = [...messages, ...newMessages].sort(
    (a, b) => a.timeStamp - b.timeStamp,
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Messages Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {fetchedAll && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Chip
              label="Beginning of Message History"
              variant="outlined"
              size="small"
              color="primary"
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {allMessages.length === 0 && !loading ? (
          <Card sx={{ textAlign: "center", py: 4, mt: 4 }}>
            <CardContent>
              <ChatIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No messages yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Start the conversation by sending a message below.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1}>
            {allMessages.map((msg, index) => renderMessage(msg, index))}
          </Stack>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Send Message Section */}
      <Box sx={{ mt: 4 }}>
        <SendPM
          receiverId={params.userId || ""}
          firstMsg={!allMessages.length}
          receiverName={location.state?.receiverName || ""}
        />
      </Box>
    </Container>
  );
}

export default ConversationLog;
